import { useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { EyeIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@ems/ui/components/sheet'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { asRegistrationForm, type FormField } from '#admin/features/registration-form/api.ts'
import { FieldDialog } from '#admin/features/registration-form/field-dialog.tsx'
import {
  AlwaysAsked,
  ArchivedList,
  FormPreview,
  QuestionList,
  StandardDetails,
} from '#admin/features/registration-form/form-builder.tsx'
import { formSettingsQuery } from '#admin/features/registration-form/queries.ts'
import { ticketTypesQuery } from '#admin/features/ticket-types/queries.ts'
import { FormBuilderSkeleton } from '#admin/features/registration-form/form-builder-skeleton.tsx'

/**
 * `/ticketing/registration-form` — what a registration for this edition asks.
 *
 * Two columns from 1024 px: the form's settings on the left, a live preview on the right that
 * stays in view while a question is shaped. Below that, one column, and the preview is a header
 * action that opens it in a sheet.
 *
 * Guarded on `ticket.view` — the form belongs to whoever runs registrations. Without
 * `ticket.manage` it is read-only: every control is removed rather than disabled, the preview
 * still works, and the notice says who to ask.
 */
export const Route = createFileRoute('/_authenticated/ticketing/registration-form')({
  pendingComponent: FormBuilderSkeleton,
  staticData: { crumb: { parents: [{ label: 'Ticketing' }], label: 'Registration form' } },
  loader: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!can(session, 'ticket.view')) throw notFound()
    await Promise.all([
      context.queryClient.ensureQueryData(formSettingsQuery()),
      context.queryClient.ensureQueryData(ticketTypesQuery()),
    ])
  },
  notFoundComponent: NotFoundView,
  component: RegistrationFormPage,
})

type Editing = { field: FormField | null } | null

function RegistrationFormPage() {
  const { data: settings } = useSuspenseQuery(formSettingsQuery())
  const { data: types } = useSuspenseQuery(ticketTypesQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'ticket.manage')

  const [editing, setEditing] = useState<Editing>(null)
  const [previewing, setPreviewing] = useState(false)

  const ticketTypes = types.items.filter((type) => type.isActive)
  const live = settings.fields.filter((field) => !field.archived)
  const archived = settings.fields.filter((field) => field.archived)
  const form = asRegistrationForm(settings)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Registration form"
        description="Choose what people fill in when they register for this edition."
        actions={
          <>
            <Button variant="outline" className="lg:hidden" onClick={() => setPreviewing(true)}>
              <EyeIcon />
              Preview
            </Button>
            {canManage ? (
              <Button onClick={() => setEditing({ field: null })}>
                <PlusIcon />
                Add field
              </Button>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the registration form" />}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)]">
        <div className="flex min-w-0 flex-col gap-6">
          <AlwaysAsked />
          <StandardDetails
            standard={settings.standard}
            defaultCountry={settings.defaultCountry}
            canManage={canManage}
          />
          <QuestionList
            fields={live}
            ticketTypes={ticketTypes}
            canManage={canManage}
            onEdit={(field) => setEditing({ field })}
            onAdd={() => setEditing({ field: null })}
          />
          <ArchivedList fields={archived} canManage={canManage} />
        </div>

        <aside
          aria-label="Preview"
          className="bg-card sticky top-[calc(env(safe-area-inset-top,0px)+1rem)] hidden max-h-[calc(100dvh-2rem)] flex-col gap-4 overflow-y-auto overscroll-contain rounded-lg border border-(--border) p-4 lg:flex"
        >
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold">Preview</h2>
            <p className="text-muted-foreground text-sm">
              One participant, as a registrant sees it.
            </p>
          </div>
          <FormPreview form={form} ticketTypes={ticketTypes} />
        </aside>
      </div>

      {editing === null ? null : (
        <FieldDialog
          // Keyed, so opening another field starts from its own values rather than the last edit.
          key={editing.field?.id ?? 'new'}
          field={editing.field}
          ticketTypes={ticketTypes}
          onClose={() => setEditing(null)}
        />
      )}

      <Sheet open={previewing} onOpenChange={setPreviewing}>
        <SheetContent className="w-full overflow-y-auto overscroll-contain sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Preview</SheetTitle>
            <SheetDescription>One participant, as a registrant sees it.</SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">
            <FormPreview form={form} ticketTypes={ticketTypes} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
